/**
 * useFeatureFlags — shared feature-flag snapshot hook (v1.0 Gate 2).
 *
 * Fetches GET /api/feature-flags once per session and caches an
 * in-memory map { flag_key: enabled_bool }. Unknown or offline flags
 * default to FALSE — fail-closed so hidden features stay hidden.
 *
 * Usage:
 *   const { flags, loading } = useFeatureFlags();
 *   if (flags.truck_features) { ... }
 */
import { useState, useEffect } from "react";
import { API, axios } from "@/api";

let cache = null;           // { flags: {...}, fetchedAt: number }
let inflight = null;        // Promise<void> when a fetch is running
const TTL_MS = 5 * 60 * 1000;
const subscribers = new Set();

async function loadFlags() {
  if (cache && Date.now() - cache.fetchedAt < TTL_MS) return cache.flags;
  if (inflight) { await inflight; return cache?.flags || {}; }

  inflight = (async () => {
    try {
      const res = await axios.get(`${API}/feature-flags`);
      const map = {};
      for (const f of res.data?.flags || []) map[f.flag_key] = !!f.enabled;
      cache = { flags: map, fetchedAt: Date.now() };
    } catch {
      // Fail-closed: empty map → every flag reads as false
      cache = { flags: {}, fetchedAt: Date.now() };
    }
    inflight = null;
    subscribers.forEach(fn => { try { fn(cache.flags); } catch {} });
  })();

  await inflight;
  return cache.flags;
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState(() => cache?.flags || {});
  const [loading, setLoading] = useState(() => !cache);

  useEffect(() => {
    let active = true;
    const onUpdate = (next) => { if (active) setFlags({ ...next }); };
    subscribers.add(onUpdate);
    loadFlags().then((next) => {
      if (!active) return;
      setFlags({ ...next });
      setLoading(false);
    });
    return () => { active = false; subscribers.delete(onUpdate); };
  }, []);

  return { flags, loading };
}

// Force-refresh helper (admin UI, after toggling a flag).
export async function refreshFeatureFlags() {
  cache = null;
  inflight = null;
  const next = await loadFlags();
  subscribers.forEach(fn => { try { fn(next); } catch {} });
  return next;
}
